import { useEffect, useState } from "react";
import QRCode from "qrcode";
import { Button, Card, CardContent, CardHeader, CardTitle, Input, Badge, ConfirmDialog } from "@wa/ui";
import { ShieldCheck, Plus, Trash2, RefreshCw, Copy, Mail } from "lucide-react";
import { useT, useI18n } from "../../../i18n/I18nProvider";
import { ApiError } from "../../../lib/api";
import { mfaApi, type MfaFactor, type TotpEnrollment } from "../../../lib/security";
import { intlLocale } from "./lib";

// ── MFA (TOTP + email OTP + backup codes) ─────────────────────────────────────
export function MfaCard({ onChanged }: { onChanged: () => void }) {
  const t = useT();
  const { locale } = useI18n();
  const [factors, setFactors] = useState<MfaFactor[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [backupStatus, setBackupStatus] = useState<{ total: number; unused: number } | null>(null);
  const [backupCodes, setBackupCodes] = useState<string[] | null>(null);

  // Confirm dialogs: the method to remove (null = closed) and the backup code renewal.
  const [removeId, setRemoveId] = useState<string | null>(null);
  const [regenOpen, setRegenOpen] = useState(false);

  // TOTP enrollment
  const [enroll, setEnroll] = useState<TotpEnrollment | null>(null);
  const [qr, setQr] = useState<string | null>(null);
  const [code, setCode] = useState("");

  // Email OTP enrollment
  const [emailEnroll, setEmailEnroll] = useState<{ factorId: string; sentTo: string } | null>(null);
  const [emailCode, setEmailCode] = useState("");

  async function reload() {
    const [f, s] = await Promise.all([
      mfaApi.listFactors().catch(() => ({ factors: [] as MfaFactor[] })),
      mfaApi.backupCodesStatus().catch(() => ({ total: 0, unused: 0 })),
    ]);
    setFactors(f.factors);
    setBackupStatus(s);
  }
  useEffect(() => {
    void reload();
  }, []);

  const activeTotp = factors?.find((f) => f.type === "totp" && f.verifiedAt && f.enabledAt);
  const activeEmail = factors?.find((f) => f.type === "email_otp" && f.verifiedAt && f.enabledAt);
  const anyActive = !!(activeTotp || activeEmail);

  function fail(err: unknown) {
    const msg = err instanceof ApiError ? t(`mfa.err.${String((err.body as { error?: string })?.error ?? "generic")}`) : t("common.error");
    setError(msg.startsWith("mfa.err.") ? t("common.error") : msg);
  }

  // ── TOTP ──────────────────────────────────────────────────────────────────
  async function startEnroll() {
    setError(null);
    setBusy(true);
    try {
      const res = await mfaApi.totpBegin();
      setEnroll(res);
      setCode("");
      setQr(await QRCode.toDataURL(res.otpauthUrl, { margin: 1, width: 220 }));
    } catch (err) {
      fail(err);
    } finally {
      setBusy(false);
    }
  }
  async function confirmEnroll() {
    if (!enroll) return;
    setError(null);
    setBusy(true);
    try {
      const res = await mfaApi.totpVerify(enroll.factorId, code.trim());
      setEnroll(null);
      setQr(null);
      setCode("");
      if (res.backupCodes) setBackupCodes(res.backupCodes);
      await reload();
      onChanged();
    } catch (err) {
      fail(err);
    } finally {
      setBusy(false);
    }
  }
  async function cancelEnroll() {
    if (!enroll) return;
    setBusy(true);
    try {
      await mfaApi.removeFactor(enroll.factorId);
    } catch {
      /* ignore */
    }
    setEnroll(null);
    setQr(null);
    setCode("");
    setBusy(false);
  }

  // ── Email OTP ───────────────────────────────────────────────────────────────
  async function startEmailEnroll() {
    setError(null);
    setBusy(true);
    try {
      setEmailEnroll(await mfaApi.emailBegin());
      setEmailCode("");
    } catch (err) {
      fail(err);
    } finally {
      setBusy(false);
    }
  }
  async function confirmEmailEnroll() {
    if (!emailEnroll) return;
    setError(null);
    setBusy(true);
    try {
      const res = await mfaApi.emailVerify(emailEnroll.factorId, emailCode.trim());
      setEmailEnroll(null);
      setEmailCode("");
      if (res.backupCodes) setBackupCodes(res.backupCodes);
      await reload();
      onChanged();
    } catch (err) {
      fail(err);
    } finally {
      setBusy(false);
    }
  }
  async function cancelEmailEnroll() {
    if (!emailEnroll) return;
    setBusy(true);
    try {
      await mfaApi.removeFactor(emailEnroll.factorId);
    } catch {
      /* ignore */
    }
    setEmailEnroll(null);
    setEmailCode("");
    setBusy(false);
  }

  async function disableFactor() {
    const id = removeId;
    if (!id) return;
    setBusy(true);
    try {
      await mfaApi.removeFactor(id);
      await reload();
      onChanged();
    } catch (err) {
      fail(err);
    } finally {
      setBusy(false);
      setRemoveId(null);
    }
  }
  async function regenerateBackup() {
    setBusy(true);
    try {
      const res = await mfaApi.regenerateBackup();
      setBackupCodes(res.codes);
      await reload();
    } catch (err) {
      fail(err);
    } finally {
      setBusy(false);
      setRegenOpen(false);
    }
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-3">
          <ShieldCheck className="size-5 text-primary" />
          <div className="flex-1">
            <CardTitle>{t("mfa.title")}</CardTitle>
            <p className="text-sm text-muted-foreground">{t("mfa.desc")}</p>
          </div>
          {anyActive && <Badge>{t("mfa.active")}</Badge>}
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {error && <p className="rounded-md bg-destructive/10 border border-destructive/30 px-3 py-2 text-sm text-destructive">{error}</p>}

        {/* TOTP enrollment flow */}
        {enroll && (
          <div className="flex flex-col gap-3 rounded-md border bg-accent/30 p-4">
            <p className="text-sm font-medium">{t("mfa.scanQr")}</p>
            <div className="flex flex-col gap-4 sm:flex-row sm:items-start">
              {qr && <img src={qr} alt="QR" width={180} height={180} className="rounded bg-white p-2" />}
              <div className="flex flex-1 flex-col gap-2 text-xs">
                <p className="text-muted-foreground">{t("mfa.secretFallback")}</p>
                <code className="select-all break-all rounded bg-muted px-2 py-1 font-mono text-[11px]">{enroll.secret}</code>
                <p className="mt-2 text-muted-foreground">{t("mfa.enterCode")}</p>
                <Input inputMode="numeric" maxLength={6} value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} placeholder="123456" className="text-center font-mono tracking-widest" />
                <div className="flex gap-2">
                  <Button onClick={confirmEnroll} loading={busy} disabled={code.length !== 6}>{t("mfa.activate")}</Button>
                  <Button variant="outline" onClick={cancelEnroll} disabled={busy}>{t("common.cancel")}</Button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Email OTP enrollment flow */}
        {emailEnroll && (
          <div className="flex flex-col gap-3 rounded-md border bg-accent/30 p-4">
            <p className="text-sm font-medium">{t("mfa.emailSentTo", { email: emailEnroll.sentTo })}</p>
            <Input inputMode="numeric" maxLength={6} value={emailCode} onChange={(e) => setEmailCode(e.target.value.replace(/\D/g, ""))} placeholder="123456" className="text-center font-mono tracking-widest" />
            <div className="flex gap-2">
              <Button onClick={confirmEmailEnroll} loading={busy} disabled={emailCode.length !== 6}>{t("mfa.activate")}</Button>
              <Button variant="outline" onClick={cancelEmailEnroll} disabled={busy}>{t("common.cancel")}</Button>
            </div>
          </div>
        )}

        {/* Existing factors + enable buttons */}
        {!enroll && !emailEnroll && factors !== null && (
          <div className="flex flex-col gap-2">
            {activeTotp ? (
              <FactorRow label={`${activeTotp.label || "TOTP"} · ${t("mfa.typeTotp")}`} sub={lastUsedSub(activeTotp, t, locale)} onRemove={() => setRemoveId(activeTotp.id)} busy={busy} />
            ) : (
              <Button onClick={startEnroll} loading={busy} variant="outline" className="self-start">
                <Plus className="mr-1 size-4" />
                {t("mfa.enableTotp")}
              </Button>
            )}

            {activeEmail ? (
              <FactorRow label={`${activeEmail.label || t("mfa.typeEmail")} · ${t("mfa.typeEmail")}`} sub={lastUsedSub(activeEmail, t, locale)} onRemove={() => setRemoveId(activeEmail.id)} busy={busy} />
            ) : (
              <Button onClick={startEmailEnroll} loading={busy} variant="outline" className="self-start">
                <Mail className="mr-1 size-4" />
                {t("mfa.enableEmail")}
              </Button>
            )}

            {!anyActive && <p className="text-sm text-muted-foreground">{t("mfa.none")}</p>}
          </div>
        )}

        {/* Backup codes */}
        {!enroll && !emailEnroll && anyActive && (
          <div className="flex flex-col gap-2 border-t pt-3">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-sm font-medium">{t("mfa.backupTitle")}</div>
                <div className="text-xs text-muted-foreground">
                  {backupStatus ? t("mfa.backupStatus", { unused: backupStatus.unused, total: backupStatus.total }) : ""}
                </div>
              </div>
              <Button variant="outline" size="sm" onClick={() => setRegenOpen(true)} disabled={busy}>
                <RefreshCw className="mr-1 size-4" />
                {t("mfa.backupRegenerate")}
              </Button>
            </div>
            {backupCodes && <BackupCodeList codes={backupCodes} onDismiss={() => setBackupCodes(null)} t={t} />}
          </div>
        )}
      </CardContent>

      <ConfirmDialog
        open={removeId !== null}
        variant="destructive"
        title={t("mfa.confirmRemove")}
        confirmLabel={t("mfa.remove")}
        cancelLabel={t("common.cancel")}
        loading={busy}
        onConfirm={() => void disableFactor()}
        onCancel={() => setRemoveId(null)}
      />
      <ConfirmDialog
        open={regenOpen}
        variant="default"
        title={t("mfa.backupConfirmRegenerate")}
        confirmLabel={t("mfa.backupRegenerate")}
        cancelLabel={t("common.cancel")}
        loading={busy}
        onConfirm={() => void regenerateBackup()}
        onCancel={() => setRegenOpen(false)}
      />
    </Card>
  );
}

function lastUsedSub(f: MfaFactor, t: (k: string, v?: Record<string, string | number>) => string, locale: string): string {
  if (f.lastUsedAt) return `${t("mfa.lastUsed")}: ${new Date(f.lastUsedAt).toLocaleDateString(intlLocale(locale))}`;
  return `${t("mfa.activatedOn")}: ${f.enabledAt ? new Date(f.enabledAt).toLocaleDateString(intlLocale(locale)) : ""}`;
}

function FactorRow({ label, sub, onRemove, busy }: { label: string; sub: string; onRemove: () => void; busy: boolean }) {
  return (
    <div className="flex items-center gap-3 rounded-md border bg-card p-3">
      <ShieldCheck className="size-4 shrink-0 text-emerald-600" />
      <div className="min-w-0 flex-1">
        <div className="text-sm font-medium">{label}</div>
        <div className="truncate text-xs text-muted-foreground">{sub}</div>
      </div>
      <Button variant="ghost" size="sm" onClick={onRemove} disabled={busy}>
        <Trash2 className="size-4 text-destructive" />
      </Button>
    </div>
  );
}

function BackupCodeList({ codes, onDismiss, t }: { codes: string[]; onDismiss: () => void; t: (k: string, v?: Record<string, string | number>) => string }) {
  const [copied, setCopied] = useState(false);
  async function copyAll() {
    await navigator.clipboard.writeText(codes.join("\n"));
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  }
  return (
    <div className="flex flex-col gap-2 rounded-md border border-amber-400/40 bg-amber-400/10 p-3">
      <p className="text-xs font-medium text-amber-700 dark:text-amber-300">⚠ {t("mfa.backupWarning")}</p>
      <ul className="grid grid-cols-2 gap-1.5 font-mono text-xs sm:grid-cols-5">
        {codes.map((c) => (
          <li key={c} className="select-all rounded border bg-card px-2 py-1 text-center">{c}</li>
        ))}
      </ul>
      <div className="flex gap-2">
        <Button variant="outline" size="sm" onClick={copyAll}>
          <Copy className="mr-1 size-4" />
          {copied ? t("mfa.copied") : t("mfa.copy")}
        </Button>
        <Button variant="ghost" size="sm" onClick={onDismiss}>{t("mfa.done")}</Button>
      </div>
    </div>
  );
}
